




import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Chart } from 'highcharts-vue';
import More from 'highcharts/highcharts-more';
import Highcharts from 'highcharts';
import vue from 'vue';
import { colors } from '@/config';
import { highchartsSpacing } from '@/utils/chartPresentationSizing';

More(Highcharts);

@Component({ components: { Chart } })
export default class DynamicLineChart extends Vue {
  @Prop() profitloss: any;
  @Prop() balance: any;
  @Prop() cashflow: any;
  @Prop() financialRatio: any;

  @Prop() dark!: boolean;
  @Prop() containerWidth: any;
  @Prop() currentLanguage: any;

  chartOptions: any = {};

  update() {
    this.$nextTick(() => {
      const chart = this.$refs.chart as any;
      if (chart && chart.chart) {
        chart.chart.reflow();
      }
    });

    this.chartOptions = {
      chart: {
        type: 'line',
        backgroundColor: 'transparent',
        ...highchartsSpacing(this.containerWidth),
      },

      credits: {
        enabled: false,
      },

      title: {
        text: '',
      },

      xAxis: {
        type: 'category',
        labels: {
          autoRotation: [0],
          staggerLines: 1,
          padding: this.transformPx(10),
          style: {
            fontSize: this.dark ? '1cqw' : '1cqw',
            textOverflow: 'none',
            whiteSpace: 'normal',
            color: this.dark ? colors.Light : colors.Black,
          },
        },
        lineColor: this.dark ? colors.Black : colors.LightGrey,
      },

      yAxis: {
        title: {
          text: '',
        },
        labels: {
          style: {
            fontSize: this.dark ? '1cqw' : '1cqw',
            color: this.dark ? colors.Light : colors.Black,
            fontWeight: this.dark ? 400 : 600,
          },
          formatter() {
            return (vue as any).options.filters.P((this as any).value);
          },
        },
        tickPixelInterval: this.dark ? this.transformPx(200) : 175,
        gridLineColor: this.dark ? colors.Black : colors.LightGrey,
        plotLines: [
          {
            color: colors.Black,
            width: this.dark ? this.transformPx(3) : 2,
            value: 0,
            zIndex: 2,
          },
        ],
        softMin: 0,
        softMax: 0,
      },

      legend: {
        verticalAlign: 'top',
        align: 'right',
        enabled: true,
        itemStyle: {
          fontSize: this.dark ? '1.5cqw' : '1cqw',
          fontWeight: 'normal',
          color: this.dark ? colors.Light : colors.Black,
        },
        itemHoverStyle: {
          color: this.dark ? colors.Light : colors.Black,
        },
        symbolWidth: this.transformPx(50),
      },

      tooltip: {
        headerFormat: '',
        formatter(): any {
          return (vue as any).options.filters.P((this as any).point.y);
        },
        style: {
          fontSize: '1.5cqw',
          color: this.dark ? colors.Light : colors.Black,
        },
        backgroundColor: this.dark ? colors.Dark : colors.Light,
      },

      series: this.getData(),
    };
  }

  mounted() {
    this.update();
  }

  @Watch('profitloss', { immediate: true, deep: true })
  profitlossChanged() {
    this.chartOptions.series = this.getData();
  }

  @Watch('balance', { immediate: true, deep: true })
  balanceChanged() {
    this.chartOptions.series = this.getData();
  }

  @Watch('cashflow', { immediate: true, deep: true })
  cashflowChanged() {
    this.chartOptions.series = this.getData();
  }

  @Watch('containerWidth', { immediate: false, deep: true })
  containerWidthChanged() {
    this.update();
  }

  getData(): any {
    switch (this.financialRatio) {
      case 'operatingMargin':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: p.K / p.A,
              }))
              .reverse(),
          },
        ];
      case 'grossMargin':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: (p.A + p.D) / p.A,
              }))
              .reverse(),
          },
        ];
      case 'valueAdded':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: (p.K - p.J - p.G) / p.A,
              }))
              .reverse(),
          },
        ];
      case 'returnOnCapital':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: p.K / this.balance.sheets[i].S,
              }))
              .reverse(),
          },
        ];
      case 'returnOnEquity':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: this.profitloss.sheets[i].N / (b.M + 0.794 * b.N),
              }))
              .reverse(),
          },
        ];
      case 'solidity':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.balance.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: (p.M + 0.794 * p.N) / p.L,
              }))
              .reverse(),
          },
        ];
      case 'quickRatio':
        if (!this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.balance.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: (p.K - p.F) / p.Q,
              }))
              .reverse(),
          },
        ];
      case 'netMargin':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: p.R / p.A,
              }))
              .reverse(),
          },
        ];
      case 'operatingCashflowPercent':
        if (!this.profitloss || !this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.OperatingCashflow / this.profitloss.sheets[i].A,
              }))
              .reverse(),
          },
        ];
      case 'freeCashflowOfNetProfit':
        if (!this.profitloss || !this.cashflow) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y:
                  (c.CashFlow - c.FinancingCashflow) /
                  this.profitloss.sheets[i].R,
              }))
              .reverse(),
          },
        ];
      case 'workingCapitalPercent':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: (b.K - b.J - b.Q + b.U) / this.profitloss.sheets[i].A,
              }))
              .reverse(),
          },
        ];
      case 'cashPercent':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.J / this.profitloss.sheets[i].A,
              }))
              .reverse(),
          },
        ];
      case 'interestBearingLiabilitiesPercent':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: (b.U + b.P) / this.profitloss.sheets[i].A,
              }))
              .reverse(),
          },
        ];
      case 'investmentsCashflowPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.InvestmentsCashflow / this.profitloss.sheets[i].A,
              }))
              .reverse(),
          },
        ];
      case 'ownersCashflowPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y: c.EquityWithoutNetProfitDelta / this.profitloss.sheets[i].A,
              }))
              .reverse(),
          },
        ];
      case 'dividentYearResultPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                // tslint:disable:max-line-length
                y:
                  -c.EquityWithoutNetProfitDelta > 0
                    ? -c.EquityWithoutNetProfitDelta /
                      this.profitloss.sheets[i + 1].R
                    : 0,
              }))
              .reverse(),
          },
        ];
      case 'dividentCashPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y:
                  -c.EquityWithoutNetProfitDelta > 0
                    ? -c.EquityWithoutNetProfitDelta /
                      this.balance.sheets[i + 1].J
                    : 0,
              }))
              .reverse(),
          },
        ];
      case 'ownerCapitalCashPercent':
        if (!this.cashflow || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.cashflow.sheets
              .map((c: any, i: number) => ({
                name:
                  c.DATE_TO.slice(5, 7) === '12'
                    ? c.DATE_TO.slice(0, 4)
                    : c.DATE_TO.slice(0, 7),
                y:
                  c.EquityWithoutNetProfitDelta +
                    c.SubscribedCapitalUnpaidDelta >
                  0
                    ? (c.EquityWithoutNetProfitDelta +
                        c.SubscribedCapitalUnpaidDelta) /
                      this.balance.sheets[i].J
                    : 0,
              }))
              .reverse(),
          },
        ];
      case 'grossProfitExPersonellCostsPercent':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: (p.A + p.D + p.G) / p.A,
              }))
              .reverse(),
          },
        ];
      case 'personellCostsPercent':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: -p.G / p.A,
              }))
              .reverse(),
          },
        ];
      case 'stockPercent':
        if (!this.profitloss || !this.balance) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.balance.sheets
              .map((b: any, i: number) => ({
                name:
                  b.DATE_TO.slice(5, 7) === '12'
                    ? b.DATE_TO.slice(0, 4)
                    : b.DATE_TO.slice(0, 7),
                y: b.F / this.profitloss.sheets[i].A,
              }))
              .reverse(),
          },
        ];
      case 'otherExternalCostsPercent':
        if (!this.profitloss) {
          return [];
        }
        return [
          {
            name: this.getName(),
            color: this.dark ? colors.Tertiary : colors.Secondary,
            dashStyle: 'dash',
            marker: {
              fillColor: this.dark ? colors.Dark : colors.Secondary,
              lineWidth: this.transformPx(2),
              lineColor: this.dark ? colors.Tertiary : colors.Primary,
              radius: this.dark ? this.transformPx(8) : 6,
            },
            pointPadding: 0,
            lineWidth: this.transformPx(2),
            data: this.profitloss.sheets
              .map((p: any, i: number) => ({
                name:
                  p.DATE_TO.slice(5, 7) === '12'
                    ? p.DATE_TO.slice(0, 4)
                    : p.DATE_TO.slice(0, 7),
                y: -p.F / p.A,
              }))
              .reverse(),
          },
        ];
    }
  }

  getName() {
    switch (this.financialRatio) {
      case 'operatingMargin':
        return this.currentLanguage === 'en'
          ? 'Operating margin'
          : Vue.prototype.$t('Rörelsemarginal');
      case 'grossMargin':
        return this.currentLanguage === 'en'
          ? 'Gross margin'
          : Vue.prototype.$t('Bruttomarginal');
      case 'valueAdded':
        return this.currentLanguage === 'en'
          ? 'Value added'
          : Vue.prototype.$t('Förädlingsgrad');
      case 'returnOnCapital':
        return this.currentLanguage === 'en'
          ? 'Return on Capital'
          : Vue.prototype.$t('Avkastning på totalt kapital');
      case 'returnOnEquity':
        return this.currentLanguage === 'en'
          ? 'Return on Equity'
          : Vue.prototype.$t('Avkastning på eget kapital');
      case 'solidity':
        return this.currentLanguage === 'en'
          ? 'Solidity'
          : Vue.prototype.$t('Soliditet');
      case 'quickRatio':
        return this.currentLanguage === 'en'
          ? 'Quick ratio'
          : Vue.prototype.$t('Kassalikviditet');
      case 'netMargin':
        return this.currentLanguage === 'en'
          ? 'Net margin'
          : Vue.prototype.$t('Nettomarginal');
      case 'freeCashflowOfNetProfit':
        return this.currentLanguage === 'en'
          ? 'Free cashflow as % of Net result'
          : Vue.prototype.$t('Fritt kassaflöde i % av Årets resultat');
      case 'operatingCashflowPercent':
        return this.currentLanguage === 'en'
          ? 'Operating cashflow in % of net sales'
          : Vue.prototype.$t('Operativa kassaflöden i % av nettoomsättningen');
      case 'workingCapitalPercent':
        return this.currentLanguage === 'en'
          ? 'Working capital (excl. cash) in % of net sales'
          : Vue.prototype.$t(
              'Bindning i rörelsekapital (exkl. kassa & bank) i % av nettoomsättningen',
            );
      case 'cashPercent':
        return this.currentLanguage === 'en'
          ? 'cash in % of Net sales'
          : Vue.prototype.$t('Kassa & bank som % av nettoomsättning');
      case 'interestBearingLiabilitiesPercent':
        return this.currentLanguage === 'en'
          ? 'Interest bearing liabilities in % of Net sales'
          : Vue.prototype.$t('Räntebärande skulder som % av nettoomsättning');
      case 'investmentsCashflowPercent':
        return this.currentLanguage === 'en'
          ? 'Investments in % of Net sales'
          : Vue.prototype.$t('Investeringar som % av nettoomsättning');
      case 'ownersCashflowPercent':
        return this.currentLanguage === 'en'
          ? 'Owners capital in % of Net sales'
          : Vue.prototype.$t('Ägarkapital som % av nettoomsättning');
      case 'dividentYearResultPercent':
        return this.currentLanguage === 'en'
          ? 'Divident in % of last year Net profit/loss'
          : Vue.prototype.$t(
              'Utdelade medel som andel av föregående periods Årets resultat',
            );
      case 'dividentCashPercent':
        return this.currentLanguage === 'en'
          ? 'Divident in % of last year cash balance'
          : Vue.prototype.$t(
              'Utdelade medel som andel av föregående periods kassa & bank',
            );
      case 'ownerCapitalCashPercent':
        return this.currentLanguage === 'en'
          ? 'Added funds from owners in % cash balance'
          : Vue.prototype.$t(
              'Under året tillfört ägarkapital som andel av UB för kassa & bank',
            );
      case 'grossProfitExPersonellCostsPercent':
        return this.currentLanguage === 'en'
          ? 'Gross profit/loss in % of Net sales'
          : Vue.prototype.$t(
              'Bruttoresultat efter personalkostnader i % av nettoomsättning',
            );
      case 'personellCostsPercent':
        return this.currentLanguage === 'en'
          ? 'Cost of personell in % of Net sales'
          : Vue.prototype.$t('Personalkostnader i % av nettoomsättning');
      case 'stockPercent':
        return this.currentLanguage === 'en'
          ? 'Stock in % of Net sales'
          : Vue.prototype.$t('Lager i % av nettoomsättning');
      case 'otherExternalCostsPercent':
        return this.currentLanguage === 'en'
          ? 'Other external costs in % of Net sales'
          : Vue.prototype.$t('Övriga externa kostnader i % av nettoomsättning');
    }
  }

  transformPx(value: any) {
    if (value * (this.containerWidth / 1920) < 1) {
      return 1;
    }
    return value * (this.containerWidth / 1920);
  }
}
